import { motion } from "framer-motion";
import BackButton from "./BackButton";
import AuthProgress from "./AuthProgress";

const particles = Array.from({ length: 10 }).map((_, index) => ({
  id: index,
  size: 10 + (index % 4) * 8,
  left: `${8 + index * 9}%`,
  top: `${10 + (index % 5) * 16}%`,
  duration: 5 + (index % 4),
}));

const AuthShell = ({
  children,
  title,
  subtitle,
  step,
  showBack = true,
}) => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.18),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(34,197,94,0.18),_transparent_30%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_45%,_#ecfdf5_100%)] px-4 py-28 sm:px-6">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {particles.map((particle) => (
          <motion.span
            key={particle.id}
            animate={{ y: [0, -18, 0], opacity: [0.25, 0.65, 0.25] }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute rounded-full bg-white/70 blur-sm"
            style={{
              width: particle.size,
              height: particle.size,
              left: particle.left,
              top: particle.top,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-8 lg:flex-row lg:items-start">
        <div className="max-w-xl space-y-6 text-slate-900">
          {showBack && <BackButton />}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <span className="inline-flex rounded-full border border-orange-200 bg-white/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.32em] text-orange-600 backdrop-blur">
              JanShakti Auth
            </span>
            <h1 className="text-4xl font-black leading-tight sm:text-5xl">
              {title}
            </h1>
            <p className="max-w-lg text-base text-slate-600 sm:text-lg">
              {subtitle}
            </p>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-white/70 bg-white/65 p-5 shadow-sm backdrop-blur-xl">
              <p className="text-sm font-semibold text-slate-900">Mobile-first flow</p>
              <p className="mt-2 text-sm text-slate-600">
                Thumb-friendly actions, clear progress, and a consistent path from signup to issue reporting.
              </p>
            </div>
            <div className="rounded-3xl border border-white/70 bg-white/65 p-5 shadow-sm backdrop-blur-xl">
              <p className="text-sm font-semibold text-slate-900">Transparent trust</p>
              <p className="mt-2 text-sm text-slate-600">
                OTP verification, inline validation, and clear feedback reduce confusion before users ever submit an issue.
              </p>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl rounded-[2rem] border border-white/70 bg-white/78 p-6 shadow-2xl shadow-orange-100/60 backdrop-blur-2xl sm:p-8"
        >
          <AuthProgress currentStep={step} totalSteps={4} />
          <div className="mt-8">{children}</div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthShell;
